import { useEffect, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { API_BASE_URL } from '@/utils/apiFetch';

export default function useFeedbackUpdates(enabled: boolean, refresh: () => void) {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (!enabled) { setConnected(false); return undefined; }
    let disposed = false;
    let pending: ReturnType<typeof setTimeout> | undefined;
    const connection = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/api/feedback-hub`, {
        withCredentials: true,
        accessTokenFactory: () => localStorage.getItem('jwt_token') || ''
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.None)
      .build();
    const reconcile = () => {
      if (disposed || document.hidden || pending) return;
      pending = setTimeout(() => { pending = undefined; if (!disposed) refresh(); }, 100);
    };
    const start = async () => {
      if (disposed || connection.state !== HubConnectionState.Disconnected) return;
      try {
        await connection.start();
        if (disposed) { await connection.stop(); return; }
        setConnected(true);
        reconcile();
      } catch { if (!disposed) setConnected(false); }
    };
    connection.on('FeedbackChanged', reconcile);
    connection.onreconnecting(() => { if (!disposed) setConnected(false); });
    connection.onclose(() => { if (!disposed) setConnected(false); });
    connection.onreconnected(() => { if (!disposed) { setConnected(true); reconcile(); } });
    const resume = () => { reconcile(); void start(); };
    void start();
    // Also covers events delivered to a different API replica without a backplane.
    const timer = setInterval(resume, 15000);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      disposed = true;
      clearInterval(timer);
      clearTimeout(pending);
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
      void connection.stop();
    };
  }, [enabled, refresh]);
  return connected;
}
